import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CandidateUpdate, CheckResult, LanguageEntry, Page, Settings } from '@clawhire/shared';
import { toIsoDate } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { EMPTY } from '@/lib/format';
import type { CandidateDetail, CheckLists, OptionLists, PartnerRef } from './types';

/**
 * Queries, mutations and small helpers shared by the candidate pages. Components stay in their own
 * files so react-refresh keeps working.
 */

export function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

export const DEFAULT_HOURS_PER_DAY = 8;
export const DEFAULT_RETENTION_MONTHS = 24;

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: () => api.get<Partial<Settings>>('/settings'),
    staleTime: 5 * 60_000,
  });
}

export function useOptions() {
  return useQuery({
    queryKey: queryKeys.options,
    queryFn: () => api.get<OptionLists>('/options'),
    staleTime: 5 * 60_000,
  });
}

/** Suppliers and freelancers (the partners a candidate can come from). */
export function useSuppliers() {
  return useQuery({
    queryKey: queryKeys.list('partners', { type: ['SUPPLIER', 'FREELANCER'] }),
    queryFn: async () => {
      const [suppliers, freelancers] = await Promise.all([
        api.get<Page<PartnerRef>>('/partners', { query: { type: 'SUPPLIER', pageSize: 200 } }),
        api.get<Page<PartnerRef>>('/partners', { query: { type: 'FREELANCER', pageSize: 200 } }),
      ]);
      return [...unwrapList<PartnerRef>(suppliers), ...unwrapList<PartnerRef>(freelancers)];
    },
    staleTime: 60_000,
  });
}

export function candidatePath(ref: string): string {
  return `/candidates/${encodeURIComponent(ref)}`;
}

export function useCandidate(ref: string | undefined) {
  return useQuery({
    queryKey: queryKeys.detail('candidates', ref ?? ''),
    queryFn: () => api.get<CandidateDetail>(candidatePath(ref!)),
    enabled: Boolean(ref),
  });
}

/** `PATCH /candidates/:id`; the response (full detail) replaces the cached detail. */
export function useUpdateCandidate(ref: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: CandidateUpdate) => api.patch<CandidateDetail>(candidatePath(ref), patch),
    onSuccess: async (detail) => {
      queryClient.setQueryData(queryKeys.detail('candidates', ref), detail);
      if (detail.id !== ref) queryClient.setQueryData(queryKeys.detail('candidates', detail.id), detail);
      if (detail.candidateId !== ref)
        queryClient.setQueryData(queryKeys.detail('candidates', detail.candidateId), detail);
      await queryClient.invalidateQueries({ queryKey: ['candidates', 'list'] });
    },
  });
}

/** `DELETE /profile-versions/:id` — removes an older profile version and its files. The caller refetches the candidate. */
export function useDeleteProfileVersion() {
  return useMutation({
    mutationFn: (id: string) => api.delete(`/profile-versions/${encodeURIComponent(id)}`),
  });
}

/** Invalidate everything candidate-related (after propose, erase, profile generation …). */
export function useInvalidateCandidates() {
  const queryClient = useQueryClient();
  return React.useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['candidates'] }),
        queryClient.invalidateQueries({ queryKey: ['submissions'] }),
        queryClient.invalidateQueries({ queryKey: ['positions'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
      ]),
    [queryClient],
  );
}

// ---------------------------------------------------------------------------
// Formatting helpers

/** "English C1 · German B1"; levels "not stated" are omitted. */
export function formatLanguages(languages: readonly LanguageEntry[] | null | undefined): string {
  if (!languages || languages.length === 0) return EMPTY;
  return languages
    .filter((l) => l.language)
    .map((l) => (l.level && l.level !== 'not stated' ? `${l.language} ${l.level}` : l.language))
    .join(' · ');
}

/** Tolerant YYYY-MM-DD (null for empty or garbage). */
export function isoDate(value: string | Date | null | undefined): string | null {
  try {
    return toIsoDate(value);
  } catch {
    return null;
  }
}

/** "375" / "375,5" -> number, null when empty or not a number. */
export function parseNumber(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const t = value.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function versionTag(version: number): string {
  return `v${String(version).padStart(2, '0')}`;
}

export function driveFolderUrl(folderId: string): string {
  return `https://drive.google.com/drive/folders/${encodeURIComponent(folderId)}`;
}

/** Normalises `checkResults` / `checks` (CheckSummary, CheckResult[] or `{ errors, warnings }`). */
export function toCheckLists(raw: unknown): CheckLists {
  const out: CheckLists = { errors: [], warnings: [] };
  const push = (r: unknown) => {
    if (!r || typeof r !== 'object') return;
    const c = r as Partial<CheckResult>;
    if (typeof c.message !== 'string') return;
    const result: CheckResult = {
      code: typeof c.code === 'string' ? c.code : 'CHECK',
      level: c.level === 'warning' ? 'warning' : 'error',
      message: c.message,
      path: typeof c.path === 'string' ? c.path : undefined,
    };
    (result.level === 'error' ? out.errors : out.warnings).push(result);
  };
  if (Array.isArray(raw)) {
    raw.forEach(push);
    return out;
  }
  if (raw && typeof raw === 'object') {
    const o = raw as { results?: unknown; errors?: unknown; warnings?: unknown; checks?: unknown };
    if (o.checks && (Array.isArray(o.checks) || typeof o.checks === 'object')) return toCheckLists(o.checks);
    if (Array.isArray(o.results)) {
      o.results.forEach(push);
      return out;
    }
    if (Array.isArray(o.errors)) o.errors.forEach((e) => push({ level: 'error', ...(e as object) }));
    if (Array.isArray(o.warnings)) o.warnings.forEach((w) => push({ level: 'warning', ...(w as object) }));
  }
  return out;
}

export function describeChecks(lists: CheckLists): string {
  const parts: string[] = [];
  if (lists.errors.length) parts.push(`${lists.errors.length} error${lists.errors.length === 1 ? '' : 's'}`);
  if (lists.warnings.length)
    parts.push(`${lists.warnings.length} warning${lists.warnings.length === 1 ? '' : 's'}`);
  return parts.length ? parts.join(', ') : 'All checks passed';
}

// ---------------------------------------------------------------------------
// Downloads and PDF previews

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Authenticated download of `/files/:id/content?download=1`. */
export async function downloadFile(fileId: string, fileName: string): Promise<void> {
  const blob = await api.blob(`/files/${encodeURIComponent(fileId)}/content`, { query: { download: 1 } });
  downloadBlob(blob, fileName);
}

export function base64ToBlob(base64: string, type = 'application/pdf'): Blob {
  const clean = base64.includes(',') ? base64.slice(base64.indexOf(',') + 1) : base64;
  const bytes = atob(clean);
  const buffer = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buffer[i] = bytes.charCodeAt(i);
  return new Blob([buffer], { type });
}

/** Object URL for `/files/:id/content` (fetched with the bearer token); revoked on unmount. */
export function usePdfObjectUrl(fileId: string | null | undefined): {
  url: string | null;
  error: string | null;
  loading: boolean;
} {
  const [url, setUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!fileId) {
      setUrl(null);
      setError(null);
      setLoading(false);
      return;
    }
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .blob(`/files/${encodeURIComponent(fileId)}/content`)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob.type ? blob : new Blob([blob], { type: 'application/pdf' }));
        setUrl(objectUrl);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId]);

  return { url, error, loading };
}

/** Object URL for an in-memory blob (preview PDFs); revoked when the blob changes or on unmount. */
export function useBlobObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return url;
}
